import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalStandard from "src/common/components/Modal/ModalStandard";
import SearchInput from "src/common/components/SearchInput/SearchInput";
import { Dropdown } from "../../../common/components/Input";
import type { ProjectGroupWithProjects } from "../../reports/hooks/useProjectGroupsWithProjects";
import type { LrsScopeMode } from "../state/lrsSlice";

interface ModuleScopePickerProps {
  isOpen: boolean;
  mode: LrsScopeMode;
  selected: number[];
  pgList: ProjectGroupWithProjects[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (next: number[], mode: LrsScopeMode) => void;
}

const GroupCheckbox = ({
  checked,
  indeterminate,
  onChange,
  label,
  count,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  label: string;
  count: string;
}) => {
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);
  return (
    <label className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-t border-gray-100 cursor-pointer">
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="cursor-pointer"
      />
      <span className="text-sm font-medium text-gray-800 truncate">{label}</span>
      <span className="ml-auto text-xs text-gray-500 shrink-0">{count}</span>
    </label>
  );
};

export const summarizeModuleScope = (selected: number[], pgList: ProjectGroupWithProjects[]) => {
  const ids = new Set(selected.map(Number));
  const groups = pgList.filter((pg) => pg.projects.some((p) => ids.has(p.project_id)));
  return { modules: ids.size, groups: groups.length };
};

const ModuleScopePicker = ({
  isOpen,
  mode,
  selected,
  pgList,
  loading,
  error,
  onClose,
  onConfirm,
}: ModuleScopePickerProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<Set<number>>(new Set());
  const [draftMode, setDraftMode] = useState<LrsScopeMode>(mode);

  useEffect(() => {
    if (isOpen) {
      setDraft(new Set(selected.map(Number)));
      setDraftMode(mode);
      setQuery("");
    }
  }, [isOpen]);

  const allModules = draftMode === "all";

  const totalModules = useMemo(
    () => new Set(pgList.flatMap((pg) => pg.projects.map((p) => p.project_id))).size,
    [pgList],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pgList
      .map((pg) => {
        const groupMatches = !q || pg.project_group_name.toLowerCase().includes(q);
        const projects = pg.projects
          .filter((p) => groupMatches || p.project_name.toLowerCase().includes(q))
          .sort((a, b) => a.project_name.localeCompare(b.project_name));
        return { ...pg, projects };
      })
      .filter((pg) => pg.projects.length > 0);
  }, [pgList, query]);

  const toggleModule = (id: number) =>
    setDraft((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const toggleGroup = (ids: number[], allChecked: boolean) =>
    setDraft((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (allChecked) {
          next.delete(id);
        } else {
          next.add(id);
        }
      }
      return next;
    });

  return (
    <ModalStandard
      id="lrs-module-scope-picker"
      fullScreen={false}
      controlledMode={{ isOpen }}
      onClose={onClose}
      showCloseButton
      minimize
      className="w-full max-w-2xl h-fit"
    >
      <h3 className="font-semibold text-base text-gray-900 mb-3 pr-6">
        {t("pages.profile.organization.lrs.pickerTitle")}
      </h3>

      <div className="mb-3">
        <Dropdown
          key={`scope-mode-${String(isOpen)}`}
          width="w-full"
          options={[
            { value: "all", optionText: t("pages.profile.organization.lrs.scopeAll") },
            { value: "selected", optionText: t("pages.profile.organization.lrs.scopeSelected") },
          ]}
          asDefaultValue
          defaultValueIndex={mode === "selected" ? 1 : 0}
          onChangeCb={(value) => setDraftMode(value === "selected" ? "selected" : "all")}
        />
      </div>

      <div
        className={`flex items-center gap-3 mb-2 ${
          allModules ? "opacity-50 pointer-events-none" : ""
        }`}
        aria-disabled={allModules}
      >
        <SearchInput
          className="grow"
          value={query}
          onChange={setQuery}
          placeholder={t("pages.profile.organization.lrs.searchModules")}
        />
        <span className="text-xs text-gray-500 shrink-0">
          {t("pages.profile.organization.lrs.selectedCount", {
            count: draft.size,
            total: totalModules,
          })}
        </span>
        <button
          type="button"
          className="text-xs text-purple-500 font-medium shrink-0 disabled:opacity-50"
          disabled={draft.size === 0}
          onClick={() => setDraft(new Set())}
        >
          {t("pages.profile.organization.lrs.clearSelection")}
        </button>
      </div>

      <div
        className={`border border-gray-200 rounded-lg bg-white h-96 overflow-y-auto personalize-scroll-visible ${
          allModules ? "opacity-50 pointer-events-none" : ""
        }`}
        aria-disabled={allModules}
      >
        {loading && (
          <div className="px-3 py-4 text-sm text-gray-400 text-center">{t("general.loading")}</div>
        )}
        {error && <div className="px-3 py-4 text-sm text-red-600 text-center">{error}</div>}
        {!loading && !error && visible.length === 0 && (
          <div className="px-3 py-4 text-sm text-gray-400 text-center">
            {t("general.noResults")}
          </div>
        )}
        {visible.map((pg) => {
          const ids = pg.projects.map((p) => p.project_id);
          const checkedCount = ids.filter((id) => draft.has(id)).length;
          const allChecked = checkedCount === ids.length;
          return (
            <div key={pg.project_group_id}>
              <GroupCheckbox
                checked={allChecked}
                indeterminate={checkedCount > 0 && !allChecked}
                onChange={() => toggleGroup(ids, allChecked)}
                label={pg.project_group_name}
                count={`${checkedCount}/${ids.length}`}
              />
              {pg.projects.map((project) => (
                <label
                  key={project.project_id}
                  className="flex items-center gap-2 pl-8 pr-3 py-1.5 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={draft.has(project.project_id)}
                    onChange={() => toggleModule(project.project_id)}
                    className="cursor-pointer"
                  />
                  <span className="text-sm text-gray-700 truncate">{project.project_name}</span>
                </label>
              ))}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end gap-3 mt-4 pt-3 border-t border-gray-200">
        <button type="button" onClick={onClose} className="btn-alternative-outline text-sm">
          {t("general.cancel")}
        </button>
        <button
          type="button"
          onClick={() => onConfirm([...draft], draftMode)}
          disabled={!allModules && draft.size === 0}
          className="btn-primary-fill text-sm"
        >
          {t("general.confirm")}
        </button>
      </div>
    </ModalStandard>
  );
};

export default ModuleScopePicker;
